import React, { useState } from 'react';
import { Modal, Steps, Upload, Button, notification, Result, Spin } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import useImportExportApi from 'hooks/useImportExportApi';
import ImportPreviewTable from './ImportPreviewTable';

const ImportWizard = ({
  open,
  onClose,
  onSuccess,
  scope,
  columns,
  config,
  humanName = 'registros',
}) => {
  const api = useImportExportApi(config);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);

  const reset = () => {
    setStep(0);
    setResult(null);
    setCommitResult(null);
    setLoading(false);
  };
  const close = () => {
    reset();
    onClose();
  };

  const handleUpload = async (file) => {
    setLoading(true);
    try {
      const res = await api.validateImport({ file, scope });
      setResult(res);
      setStep(1);
    } catch (e) {
      notification.error({
        message: 'No se pudo validar el archivo',
        description: e?.response?.data?.message || e.message,
      });
    } finally {
      setLoading(false);
    }
    return false; // evita el upload automático de antd
  };

  const handleCommit = async () => {
    setLoading(true);
    try {
      const rows = result.rows
        .filter((r) => r.action !== 'error')
        .map((r) => ({ line: r.line, action: r.action, data: r.data }));
      const out = await api.commitImport({
        validationToken: result.validationToken,
        dataHash: result.dataHash,
        rows,
      });
      setCommitResult(out);
      setStep(2);
      onSuccess?.();
    } catch (e) {
      notification.error({
        message: 'No se pudo aplicar la importación',
        description: e?.response?.data?.message || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      width={900}
      title={`Importar ${humanName}`}
      destroyOnHidden
    >
      <Steps
        current={step}
        className="mb-4"
        items={[{ title: 'Subir' }, { title: 'Revisar' }, { title: 'Listo' }]}
      />
      <Spin spinning={loading}>
        {step === 0 && (
          <Upload.Dragger
            accept=".xlsx,.csv"
            beforeUpload={handleUpload}
            showUploadList={false}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Arrastre el archivo Excel o CSV, o haga clic para seleccionarlo
            </p>
            <p className="ant-upload-hint">Máximo 10 MB / 5000 filas.</p>
          </Upload.Dragger>
        )}
        {step === 1 && result && (
          <>
            <ImportPreviewTable result={result} columns={columns} />
            <div className="flex justify-end gap-2 mt-3">
              <Button onClick={() => setStep(0)}>Volver a subir</Button>
              <Button type="primary" onClick={handleCommit} disabled={result.summary.ok === 0}>
                Confirmar ({result.summary.inserts + result.summary.updates} cambios)
              </Button>
            </div>
          </>
        )}
        {step === 2 && commitResult && (
          <Result
            status="success"
            title={`Importación de ${humanName} aplicada`}
            subTitle={`${commitResult.inserted} creadas · ${commitResult.updated} actualizadas · ${commitResult.deactivated} desactivadas`}
            extra={
              <Button type="primary" onClick={close}>
                Cerrar
              </Button>
            }
          />
        )}
      </Spin>
    </Modal>
  );
};

export default ImportWizard;
